import React, { useCallback, useEffect, useRef } from 'react';
import { Button, List, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import SearchResultItem from './SearchResultItem.jsx';
import PropTypes from 'prop-types';
import Spinner from '../../../../common/spinner/Spinner.jsx';

const SearchResultWrapper = ({
  data,
  simpleResults,
  onSelect,
  loadMore,
  loading,
}) => {
  const { t } = useTranslation();
  const sentinelRef = useRef(null);
  // Ref to prevent duplicate load calls.
  const isFetchingRef = useRef(false);

  const renderLoadMore =
    !loading && data?.hits.length < data?.total ? (
      <div
        ref={sentinelRef}
        style={{
          textAlign: 'center',
          marginTop: 12,
          height: 32,
          lineHeight: '32px',
        }}
      >
        <Button
          onClick={() =>
            loadMore(data?.hits[data?.hits.length - 1].searchAfter)
          }
        >
          loading more
        </Button>
      </div>
    ) : data?.hits.length < data?.total ? (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxHeight: 100,
        }}
      >
        <Spinner />
      </div>
    ) : null;

  // This callback will be called when the sentinel comes into view.
  const handleIntersection = useCallback(
    (entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          data &&
          data.hits &&
          data.hits.length > 0 &&
          loadMore && // ensure loadMore function is provided
          !loading &&
          !isFetchingRef.current
        ) {
          // Get the last hit's searchAfter array.
          const lastHit = data.hits[data.hits.length - 1];
          if (lastHit.searchAfter) {
            isFetchingRef.current = true;
            Promise.resolve(loadMore(lastHit.searchAfter)).finally(() => {
              isFetchingRef.current = false;
            });
          }
        }
      });
    },
    [data, loadMore, loading],
  );

  // Set up the IntersectionObserver to watch the sentinel element.
  useEffect(() => {
    if (!simpleResults) {
      const observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.5, // Adjust threshold as needed
      });
      // Delay the observation to avoid duplicate triggers
      const timeoutId = setTimeout(() => {
        if (sentinelRef.current) {
          observer.observe(sentinelRef.current);
        }
      }, 300); // Adjust delay (300ms in this example)

      return () => {
        clearTimeout(timeoutId);
        if (sentinelRef.current) {
          observer.unobserve(sentinelRef.current);
        }
      };
    }
  }, [handleIntersection, simpleResults]);

  return (
    <>
      {/*Show total hits and time took */}
      {(data.total || data.took) && (
        <div style={{ marginTop: 2 }}>
          <Typography
            style={{ color: 'gray', userSelect: 'none', fontSize: 12 }}
          >
            {data.total} {t('nájdených výsledkov za')} {data.took} ms
          </Typography>
        </div>
      )}

      {/* Results Section */}
      <List
        style={{
          marginTop: 16,
          //maxHeight: '70vh',
          //overflow: 'auto',
          background: simpleResults ? '' : 'white',
          padding: simpleResults ? '' : 16,
          boxShadow: simpleResults ? '' : '0px 0px 10px rgba(0, 0, 0, 0.1)',
        }}
        itemLayout={'horizontal'}
        loading={loading}
        loadMore={!simpleResults && renderLoadMore}
        dataSource={data?.hits}
        renderItem={(item) => (
          <List.Item>
            <SearchResultItem
              onSelect={onSelect}
              hit={item}
              simpleResults={simpleResults}
            />
          </List.Item>
        )}
      />
    </>
  );
};

SearchResultWrapper.propTypes = {
  data: PropTypes.object,
  simpleResults: PropTypes.bool,
  onSelect: PropTypes.func,
  loadMore: PropTypes.func,
  loading: PropTypes.bool,
};

export default SearchResultWrapper;
